import {
  AlertCircle,
  BarChart3,
  Brain,
  Calendar,
  CalendarCheck,
  CalendarOff,
  Clock,
  FolderOpen,
  Contact,
  Home,
  LayoutDashboard,
  Mail,
  LayoutGrid,
  Plug,
  Monitor,
  MonitorDown,
  Network,
  Package,
  Settings,
  SlidersHorizontal,
  UserCircle,
  Users,
  Wallet,
  Workflow,
  X,
  MessageCircle,
  Target,
  CheckSquare,
  Briefcase,
  AppWindow,
  GitBranch,
  ShieldCheck,
  Inbox,
  Bell,
  ClipboardCheck,
  FolderKanban,
  GraduationCap,
  BrainCircuit,
  Lightbulb,
} from 'lucide-react';
import React, { useEffect, useLayoutEffect, useState, useRef, useCallback, useMemo } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useApi } from '../../hooks/useApi';
import toast from 'react-hot-toast';
import { useTheme } from '../../contexts/ThemeContext';
import { Modal } from 'antd';
import { AlertTriangle, Save } from 'lucide-react';
import FloatingChatButton from '../../components/FloatingChatButton/FloatingChatButton';
import FloatingSuggestButton from '../../components/FloatingSuggestButton/FloatingSuggestButton';
import PlanExpiredOverlay from './components/PlanExpiredOverlay';
// PAGE_LOADER_PARKED 2026-08-30: `import { PageLoader } from './components/PageLoader'` + `<PageLoader />` inside <main> re-enables the TeamGrid page-switch loader.
import { useGoalsSettings } from '../../contexts/GoalsSettingsContext';
import { isS5Organisation } from '../../utils/organization';
import { canUserAccessCrm } from '../../modules/crm/utils/orgGating';
import { useMyCrmRole } from '../../modules/crm/hooks/useTeamMembers';
import { isStealthIntelligenceEnabledForOrg, isStealthUnderCrmOrg } from '../../modules/stealth-agent/orgGating';
import { DASHBOARD_BASE } from '../../config/featureFlags';
import { AppSidebar } from './components/AppSidebar';
import { AppTopBar } from './components/AppTopBar';
import { useOrgReminderPoll } from '../../modules/tasks/hooks/useOrgReminderPoll';


const SIDEBAR_COLLAPSED_KEY = 'tg.sidebarCollapsed';

const Layout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try { return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === '1'; } catch { return false; }
  });
  const toggleSidebarCollapsed = useCallback(() => {
    setSidebarCollapsed((v) => {
      const next = !v;
      try { localStorage.setItem(SIDEBAR_COLLAPSED_KEY, next ? '1' : '0'); } catch (_) {}
      return next;
    });
  }, []);
  // One control in the top bar: on desktop it collapses the panel, on mobile
  // there is no panel to collapse so it opens the drawer instead.
  const toggleSidebar = useCallback(() => {
    if (window.matchMedia('(max-width: 767px)').matches) setSidebarOpen((v) => !v);
    else toggleSidebarCollapsed();
  }, [toggleSidebarCollapsed]);
  const [organization, setOrganization] = useState(null);
  const [orgLoaded, setOrgLoaded] = useState(false);
  const mainRef = useRef(null);
  const { user, logout } = useAuth();

  // OLD_GOALS_PREVIEW: the legacy Reports/Tasks page is limited to one org.
  // Single source of truth for both the sidebar entry and the redirect guard.
  // organizationId arrives either as a plain id or as a populated { _id, name }
  // object depending on the endpoint, so normalise before comparing — same shape
  // handling as normalizeOrgId in modules/stealth-agent/orgGating.js.
  const OLD_TASKS_ALLOWED_ORG_ID = '693abb1768c80fc88c425aa7'; // CCRAFT IDEA
  const rawOldTasksOrg = user?.organizationId;
  const oldTasksOrgId = (rawOldTasksOrg && typeof rawOldTasksOrg === 'object')
    ? (rawOldTasksOrg._id || rawOldTasksOrg.id || null)
    : rawOldTasksOrg;
  const canSeeOldTasks = String(oldTasksOrgId || '') === OLD_TASKS_ALLOWED_ORG_ID;
  const navigate = useNavigate();
  const location = useLocation();
  const api = useApi();
  useOrgReminderPoll(organization?.tasksEnabled === true);

  // Scroll main content to top on route change (dashboard/app pages)
  useLayoutEffect(() => {
    if (mainRef.current) {
      mainRef.current.scrollTop = 0;
    }
  }, [location.pathname]);
  const { mode, toggle } = useTheme();
  const isDark = mode === 'dark';

  // CRM sub-nav lives in the sidebar, so the rail needs the viewer's CRM role
  // to decide whether the manager-only Review tab is listed. Only asked for
  // when the org actually has CRM.
  const crmEnabled = canUserAccessCrm(user, organization);
  const { isAdmin: isCrmAdmin } = useMyCrmRole({ enabled: crmEnabled });
  
  // Get goals settings - context should be available as Layout is wrapped in GoalsSettingsProvider
  const goalsSettingsContext = useGoalsSettings();
  const goalsSettings = goalsSettingsContext?.settings || { myGoalsEnabled: false, myTasksEnabled: false };
  const goalsSettingsLoaded = goalsSettingsContext?.loaded ?? false;
  const hasUnsavedGoalsChanges = goalsSettingsContext?.hasUnsavedChanges ?? false;
  const resetGoalsToSaved = goalsSettingsContext?.resetToSaved;
  const saveGoalsSettings = goalsSettingsContext?.saveSettings;

  // Pending approvals badge on the Home → Idle time requests row. Admin-only
  // because only admins can act on the queue; a count nobody can clear is just noise.
  const isOrgAdmin = user?.role === 'org_admin' || user?.role === 'main_admin';
  const [pendingApprovals, setPendingApprovals] = useState(0);
  useEffect(() => {
    if (!isOrgAdmin) return undefined;
    const ctrl = new AbortController();
    api.get('/api/v1/idle-requests?status=pending&page=1', { signal: ctrl.signal })
      .then((res) => { if (res.data?.success) setPendingApprovals(res.data.data.total || 0); })
      .catch(() => {});
    return () => ctrl.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOrgAdmin, location.pathname]);

  // Unsaved changes guard state (for navigating away from Goal Settings)
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const [savingOnLeave, setSavingOnLeave] = useState(false);
  const pendingNavHref = useRef(null);

  // Goals & Tasks section - default collapsed
  // Fetch organization data to check plan
  const fetchOrganization = useCallback(async (forceRefresh = false) => {
    if (user && user.id) {
      try {
        // Clear cache if force refresh
        if (forceRefresh && typeof window !== 'undefined') {
          try {
            delete window.__orgProfile;
            delete window.__orgProfileFetchedAt;
          } catch (_) {}
        }
        
        // Always fetch fresh data (don't use cache) to ensure latest bypass status
        const response = await api.get('/api/v1/billing/profile');
        if (response.data.success) {
          const orgData = response.data.data;
          console.log('🔍 [LAYOUT] Organization data loaded:', {
            paymentBypassEnabled: orgData.paymentBypassEnabled,
            orgPlanSelection: orgData.orgPlanSelection,
            subscriptionStatus: orgData.subscriptionStatus,
            userIsActivated: orgData.userIsActivated,
            fullData: orgData
          });
          setOrganization(orgData);
          try {
            window.__orgProfile = orgData;
            window.__orgProfileFetchedAt = Date.now();
          } catch (_) {}
        }
        setOrgLoaded(true);
      } catch (error) {
        console.error('Error fetching organization data:', error);
        setOrgLoaded(true);
      }
    }
  }, [user?.id]); // Removed 'api' - it's stable via useMemo in useApi hook

  useEffect(() => {
    fetchOrganization();
  }, [fetchOrganization]);

  // Listen for payment success to refresh organization data
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const payment = urlParams.get('payment');

    // Match legacy Dashboard behaviour: fire on payment=success regardless of
    // whether orgId is present (the Razorpay renewal path redirects without
    // orgId). Sync BOTH gateways like the original Dashboard did.
    if (payment === 'success') {
      // Clear cache and sync subscription from Stripe
      if (typeof window !== 'undefined') {
        try {
          delete window.__orgProfile;
          delete window.__orgProfileFetchedAt;
        } catch (_) {}
      }

      // Try to manually sync subscription from both gateways (Stripe + Razorpay)
      const syncSubscription = async () => {
        try {
          await api.post('/api/v1/billing/sync-subscription');
        } catch (syncError) {
          console.warn('⚠️ [LAYOUT] Stripe sync failed, will rely on webhook:', syncError);
        }
        try {
          await api.post('/api/v1/razorpay-billing/sync-subscription');
        } catch (syncError) {
          console.warn('⚠️ [LAYOUT] Razorpay sync failed, will rely on webhook:', syncError);
        }
        console.log('✅ [LAYOUT] Manual subscription sync completed');
      };
      
      // Sync immediately
      syncSubscription();
      
      // Clear cache and refresh after webhook processes
      setTimeout(async () => {
        await syncSubscription();
        await fetchOrganization(true);
        // Refresh again to ensure data is synced
        setTimeout(async () => {
          await syncSubscription();
          await fetchOrganization(true);
        }, 2000);
      }, 1000);
    }
  }, [location.search, fetchOrganization, api]);

  // Redirect main admin users to main admin panel (unless they are in switch mode)
  useEffect(() => {
    if (user?.role === 'main_admin' && !user?.isMainAdminSwitch && location.pathname.startsWith('/dashboard')) {
      navigate('/main-admin', { replace: true });
    }
  }, [user, location.pathname, navigate]);


  // Check if user can manage projects based on permissions OR position hierarchy
  const canManageProjects = () => {
    // Main admin and org admin can always manage projects
    if (user?.role === 'main_admin' || user?.role === 'org_admin') {
      return true;
    }

    // Assigned to at least one project? Then the section is useful to them —
    // they can see their own projects even without the create right. The page
    // itself still hides "New project" unless addProject is granted.
    // (assignedProjectCount comes from /auth/me.)
    if ((user?.assignedProjectCount || 0) > 0) {
      return true;
    }

    // For org_user, check permissions only (position-based check removed)
    if (user?.role === 'org_user') {
      // If user has addProject permission, allow access
      if (user?.permissions?.addProject) {
        return true;
      }

      // Position-based check commented out - now only permission-based
      // Otherwise, check position hierarchy (keep existing logic as fallback)
      // const projectManagementPositions = [
      //   'CEO',
      //   'CTO',
      //   'VP',
      //   'Director',
      //   'Manager',
      //   'Project Manager',
      //   'Product Manager',
      //   'Scrum Master',
      //   'Engineering Manager',
      //   'Team Lead',
      //   'team lead',
      //   'team_lead',
      //   'Lead',
      //   'tech lead',
      //   'tech_lead'
      // ];

      // // Developer positions that should NOT manage projects
      // const juniorDeveloperPositions = [
      //   'intern',
      //   'junior',
      //   'jr'
      // ];

      // const userPosition = user?.position?.toLowerCase() || '';

      // // If position contains junior developer keywords, they can't manage projects
      // if (juniorDeveloperPositions.some(devPos => userPosition.includes(devPos))) {
      //   return false;
      // }

      // // Check if position contains management keywords
      // return projectManagementPositions.some(position =>
      //   userPosition.includes(position.toLowerCase())
      // );
      
      // Return false if no addProject permission
      return false;
    }
    
    return false;
  };

  // Compute restricted mode (free trial expired or paid plan invalid/expired)
  // Checks both user-level AND organization-level subscription expiry
  const restricted = React.useMemo(() => {
    if (!organization) return false;
    
    // Check if organization has payment bypass enabled - if yes, no restrictions
    if (organization.paymentBypassEnabled === true) {
      console.log('✅ [LAYOUT RESTRICTED] Payment bypass enabled - no restrictions', {
        paymentBypassEnabled: organization.paymentBypassEnabled,
        orgPlanSelection: organization.orgPlanSelection
      });
      return false; // No restrictions if bypass enabled
    }
    
    console.log('⚠️ [LAYOUT RESTRICTED] Payment bypass check:', {
      paymentBypassEnabled: organization.paymentBypassEnabled,
      orgPlanSelection: organization.orgPlanSelection,
      subscriptionStatus: organization.subscriptionStatus
    });
    
    const now = new Date();
    // If bypass enabled but orgPlanSelection is null, treat as premium (no restrictions)
    const effectivePlan = organization.paymentBypassEnabled ? 'premium' : organization.orgPlanSelection;
    const isFree = effectivePlan === 'free';
    
    // User-level subscription check
    const userStatus = organization.userSubscriptionStatus;
    const userEndDate = organization.userSubscriptionEndDate ? new Date(organization.userSubscriptionEndDate) : null;
    // Treat userSubscriptionEndDate as trialEnd for free plan (backend sets it)
    const freeExpired = isFree && userEndDate && now > userEndDate;
    const userPaidInvalid = !isFree && (
      ['past_due','canceled','incomplete_expired','unpaid','expired'].includes(userStatus) ||
      (userEndDate && now > userEndDate)
    );
    
    // Organization-level subscription check (for all members)
    const orgStatus = organization.subscriptionStatus; // Organization-level subscription status
    const orgPeriodEnd = organization.currentPeriodEnd ? new Date(organization.currentPeriodEnd) : null;
    // Fallback to trialEnd if currentPeriodEnd is not set and status is trialing
    const effectivePeriodEnd = orgPeriodEnd || (orgStatus === 'trialing' && organization.trialEnd ? new Date(organization.trialEnd) : null);
    
    // Check if subscription is actually active
    // Active means: periodEnd is in the future (not expired)
    const hasActiveSubscription = effectivePeriodEnd && now < effectivePeriodEnd;
    
    // Check if subscription is expired
    // IMPORTANT: If periodEnd is in future, subscription is active regardless of status
    // This handles the case where webhook hasn't updated status yet but periodEnd is updated
    const orgExpired = !isFree && !hasActiveSubscription && (
      ['past_due','canceled','incomplete_expired','unpaid','expired'].includes(orgStatus) ||
      (effectivePeriodEnd && now > effectivePeriodEnd) ||
      !effectivePeriodEnd // No period end means expired
    );
    
    // If organization subscription expired, restrict ALL members
    const isRestricted = Boolean(freeExpired || userPaidInvalid || orgExpired);
    
    console.log('🔍 [LAYOUT RESTRICTED] Final restricted check:', {
      restricted: isRestricted,
      freeExpired,
      userPaidInvalid,
      orgExpired,
      isFree,
      userStatus,
      orgStatus
    });
    
    return isRestricted;
  }, [organization]);

  // ── Free-trial days remaining (parity with legacy Dashboard banner) ──
  // Only shown for an active free-plan trial (orgPlan 'free', status 'trialing',
  // trialEnd in the future). Org-level data only, so org_admin + org_user see
  // the same thing.
  const trialDaysRemaining = useMemo(() => {
    if (!organization) return null;
    const orgPlan = organization.orgPlanSelection;
    const orgSubscriptionStatus = organization.subscriptionStatus;
    const paidPlans = ['startup', 'growth', 'premium'];
    if (paidPlans.includes(orgPlan)) return null;
    if (orgSubscriptionStatus === 'active') return null;
    if (orgPlan !== 'free' || orgSubscriptionStatus !== 'trialing' || !organization.trialEnd) return null;
    const now = new Date();
    const trialEnd = new Date(organization.trialEnd);
    const daysRemaining = Math.ceil((trialEnd - now) / (1000 * 60 * 60 * 24));
    if (trialEnd < now || daysRemaining < 0) return null;
    return daysRemaining;
  }, [organization]);

  const isActive = useCallback((href) => {
    return location.pathname === href;
  }, [location.pathname]);

  // Active main-admin switch only (not a stuck DB flag on real org_admin).
  // Matches "Back to Main Admin" gating — localStorage session + user flag.
  const isActiveMainAdminSwitch =
    Boolean(user?.isMainAdminSwitch) &&
    localStorage.getItem('isMainAdminSwitch') === 'true';
  const isMainAdminDevAccess =
    user?.role === 'main_admin' || isActiveMainAdminSwitch;

  // Dev-only navigation — main_admin / active switch only (never plain org_admin)
  const devNavigation = [
    // ADDITIVE: Stealth Monitor (main_admin / active switch only) — isolated module.
    ...(isMainAdminDevAccess ? [
      { name: 'Stealth · Org Intelligence', href: `${DASHBOARD_BASE}/stealth/org-intelligence`, icon: Brain },
      { name: 'Stealth · Work Summary', href: `${DASHBOARD_BASE}/stealth/work-summary`, icon: Calendar },
      { name: 'Stealth · Devices', href: `${DASHBOARD_BASE}/stealth/devices`, icon: Monitor },
    ] : []),
  ];

  // Navigation: restrict to Dashboard + Settings when in restricted mode
  const baseSections = [
    {
      key: 'work',
      name: 'Work',
      icon: Home,
      items: [
        { name: 'Dashboard', href: `${DASHBOARD_BASE}`, icon: Home },
        { name: 'Settings', href: `${DASHBOARD_BASE}/settings`, icon: Settings },
      ],
    },
  ];

  // ── Two-level nav ───────────────────────────────────────────────────────
  // Level 1 is the icon rail (a section per rail tile); level 2 is the panel
  // listing that section's items. Every href, lock and gate below is carried
  // over unchanged from the flat menu — only the grouping is new.
  const fullSections = (user?.role === 'main_admin')
    ? [
        {
          key: 'admin',
          name: 'Admin',
          icon: BarChart3,
          items: [
            { name: 'Main Admin Panel', href: '/main-admin', icon: BarChart3 },
            { name: 'Error Logs', href: '/main-admin/error-logs', icon: AlertCircle },
          ],
        },
      ]
    : [
        {
          key: 'home',
          name: 'Home',
          icon: Home,
          items: [
            { name: 'Dashboard', href: `${DASHBOARD_BASE}`, icon: Home, primary: true },
            ...(canManageProjects()
              ? [{ name: 'Projects', href: `${DASHBOARD_BASE}/projects`, icon: FolderOpen }]
              : []),
            { name: 'Team', href: `${DASHBOARD_BASE}/team`, icon: Users },
            // ADDITIVE: Tasks module — shown only when main_admin enabled it for this org.
            ...(organization?.tasksEnabled === true
              ? [{ name: 'Tasks', href: `${DASHBOARD_BASE}/tasks`, icon: CheckSquare }]
              : []),
            { name: 'Org structure', href: `${DASHBOARD_BASE}/org-structure`, icon: Network },
            // Only admins can act on the queue, so only they see it.
            ...(isOrgAdmin
              ? [{ name: 'Idle time requests', href: `${DASHBOARD_BASE}/inbox`, icon: Clock, badge: pendingApprovals || undefined }]
              : []),
          ],
        },
        // Goals / Tasks keep the same S5 + settings gating as before.
        // Admins always see the section so Goals Settings stays reachable
        // even when the My Goals module itself is toggled off.
        ...((goalsSettingsLoaded && isS5Organisation(user)
          && (goalsSettings?.myGoalsEnabled || goalsSettings?.myTasksEnabled || isOrgAdmin))
          ? [{
              key: 'goals',
              name: 'Goals',
              icon: Target,
              items: [
                ...(goalsSettings?.myGoalsEnabled
                  ? [
                      { name: 'My Goals', href: `${DASHBOARD_BASE}/goals`, icon: Target, primary: true },
                      { name: 'Team Goals', href: `${DASHBOARD_BASE}/goals/team`, icon: Users },
                      { name: 'Projects', href: `${DASHBOARD_BASE}/goals/projects`, icon: FolderKanban },
                    ]
                  : []),
                ...(goalsSettings?.myTasksEnabled
                  ? [{ name: 'My Tasks', href: `${DASHBOARD_BASE}/tasks`, icon: CheckSquare }]
                  : []),
                ...(isOrgAdmin
                  ? [{ name: 'Settings', href: `${DASHBOARD_BASE}/goals/settings`, icon: Settings }]
                  : []),
                // OLD_GOALS_PREVIEW — legacy /reports page, "Tasks · Classic".
                // Sirf ek org ko dikhta hai; redirect guard neeche already maujood hai
                // aur App.js mein `reports` route bhi. Revert = ye 3 line hatao.
                ...(canSeeOldTasks
                  ? [{ name: 'Tasks', href: `${DASHBOARD_BASE}/reports`, icon: Target, badge: 'Classic' }]
                  : []),
              ],
            }]
          : []),
        {
          key: 'intelligence',
          // "Intelligence" wraps in the 58px rail, so this shows as "Insights" —
          // same length as "Settings", which already fits.
          name: 'Insights',
          // Not Brain: the section's own "Org Intelligence" child already carries
          // a brain, and the label is deliberately plain rather than AI-flavoured.
          icon: Lightbulb,
          items: [
            ...(isStealthIntelligenceEnabledForOrg(user?.organizationId) && !isStealthUnderCrmOrg(user?.organizationId)
              ? [
                  { name: 'Stealth Work Summary', href: `${DASHBOARD_BASE}/work-summary`, icon: Calendar, primary: true },
                  { name: 'Stealth Org Intelligence', href: `${DASHBOARD_BASE}/org-intelligence`, icon: Brain },
                ]
              : [
                  // { name: 'Work Summary', href: `${DASHBOARD_BASE}/work-summary`, icon: Calendar, primary: true },
                  // ADDITIVE: the two-source rebuild. Sits beside the original,
                  // which keeps its link, its route and its page unchanged.
                  { name: 'Work Summary', href: `${DASHBOARD_BASE}/work-summary-new`, icon: Calendar, badge: 'New' },
                  // Nav-only trim (2026-08-11): two entries were dropped from this list —
                  // Work Intelligence (`/work-intelligence`) and the legacy Org Intelligence
                  // (`/org-intelligence`, which carried the standard-plan lock). Both PAGES and
                  // their routes are untouched and still live; only the sidebar links are gone,
                  // so existing links and bookmarks keep working.
                  // "Org Intelligence" is now the label for the Company Brain page.
                  // { name: 'Org Intelligence', href: `${DASHBOARD_BASE}/company-brain`, icon: BrainCircuit, badge: 'New' },
                  // ADDITIVE: same page, two-source data. The original keeps its link.
                  { name: 'Org Intelligence', href: `${DASHBOARD_BASE}/org-intelligence-v2`, icon: BrainCircuit, badge: 'New' },
                ]),
            ...(isStealthUnderCrmOrg(user?.organizationId)
              ? [
                  { name: 'Stealth Work Summary', href: `${DASHBOARD_BASE}/stealth/work-summary`, icon: Calendar },
                  { name: 'Stealth Org Intelligence', href: `${DASHBOARD_BASE}/stealth/org-intelligence`, icon: Brain },
                ]
              : []),
            { name: 'App Usage', href: `${DASHBOARD_BASE}/app-usage`, icon: AppWindow, soon: true, badge: 'Soon' },
            { name: 'Skill Profiles', href: `${DASHBOARD_BASE}/skill-profiles`, icon: GraduationCap },
            // TEMP 2026-09-07: Living tool hidden from Insights. Route + page stay.
            // { name: 'Living tool', href: `${DASHBOARD_BASE}/living-tool`, icon: ClipboardCheck, badge: 'New' },
            { name: 'Ask TG', href: `${DASHBOARD_BASE}/ask`, icon: MessageCircle, badge: 'Beta' },
          ],
        },

        // CRM — same org+user gating as before. The whole tile disappears when
        // the org has crmEnabled off, since an empty section is filtered out.
        ...(crmEnabled
          ? [{
              key: 'crm',
              name: 'CRM',
              icon: Briefcase,
              items: [
                { name: 'Dashboard', href: `${DASHBOARD_BASE}/crm`, icon: LayoutDashboard, primary: true, badge: 'Preview' },
                { name: 'Leads', href: `${DASHBOARD_BASE}/crm/leads`, icon: Users },
                { name: 'Pipeline', href: `${DASHBOARD_BASE}/crm/pipeline`, icon: GitBranch },
                // Workflows are per-project, so they live in CRM Settings →
                // Projects → Workflows rather than as a top-level destination.
                { name: 'Onboarding', href: `${DASHBOARD_BASE}/crm/onboarding`, icon: ShieldCheck },
                { name: 'Inbox', href: `${DASHBOARD_BASE}/crm/inbox`, icon: Inbox },
                { name: 'Follow-ups', href: `${DASHBOARD_BASE}/crm/followups`, icon: Bell },
                { name: 'Activities', href: `${DASHBOARD_BASE}/crm/activities`, icon: Calendar },
                // Manager-only: /dashboard/manager-review 403s for agent/viewer.
                ...(isCrmAdmin ? [{ name: 'Review', href: `${DASHBOARD_BASE}/crm/review`, icon: ClipboardCheck }] : []),
                { name: 'CRM Settings', href: `${DASHBOARD_BASE}/crm/settings`, icon: SlidersHorizontal },
              ],
            }]
          : []),
        // Apps — team app-usage monitoring. Started as Gmail-only ("Email");
        // renamed+generalized so Jira/Asana/Slack/Teams connectors can land
        // here later without another rename. Gmail is the only live
        // connector today (see modules/apps/appCatalog.js) — the other two
        // pages are catalog-driven so adding a real connector is additive.
        {
          key: 'email',
          name: 'Apps',
          icon: LayoutGrid,
          items: [
            { name: 'Emails', href: `${DASHBOARD_BASE}/email-insights`, icon: Mail, primary: true },
            // `soon` is NOT `locked`: locked fires the plan-upgrade toast and sends
            // the user to billing. These two are simply not being offered yet, so
            // they render dimmed, don't navigate, and stay out of the jump search.
            // The pages and routes are untouched — drop the flag to bring them back.
            { name: 'Team Apps', href: `${DASHBOARD_BASE}/email/gmail`, icon: Users, soon: true, badge: 'Soon' },
            { name: 'Connectors', href: `${DASHBOARD_BASE}/apps/connectors`, icon: Plug, soon: true, badge: 'Soon' },
          ],
        },
        // HRMS_NAV_HIDDEN (2026-08-17): section commented out of the nav on
        // request — HRMS has no org-level gate (unlike CRM's crmEnabled), so
        // it was showing for EVERY org. Routes and backend stay untouched;
        // direct /dashboard/hrms URLs still work. To restore, uncomment this
        // block. If it comes back, consider an hrmsEnabled org flag first.
        //
        // HRMS — the HR system of record. Ordered by how often HR touches it:
        // the daily screen, then self-service, then the master record, then the
        // three operational engines (time, absence, money), then the lifecycle
        // pipelines, then services, with config pinned last. HR config lives
        // here rather than in the Settings rail so HR admins never leave HRMS.
        // {
        //   key: 'hrms',
        //   name: 'HRMS',
        //   icon: Contact,
        //   items: [
        //     { name: 'Overview', href: `${DASHBOARD_BASE}/hrms`, icon: LayoutDashboard, primary: true },
        //     { name: 'My HR', href: `${DASHBOARD_BASE}/hrms/me`, icon: UserCircle },
        //     { name: 'People', href: `${DASHBOARD_BASE}/hrms/people`, icon: Contact },
        //     { name: 'Attendance', href: `${DASHBOARD_BASE}/hrms/attendance`, icon: CalendarCheck },
        //     { name: 'Leave', href: `${DASHBOARD_BASE}/hrms/leave`, icon: CalendarOff },
        //     // Payroll and HR config are HR-admin surfaces end to end — an
        //     // employee opening them only ever gets refused reads.
        //     ...(isOrgAdmin
        //       ? [{ name: 'Payroll', href: `${DASHBOARD_BASE}/hrms/payroll`, icon: Wallet }]
        //       : []),
        //     { name: 'Lifecycle', href: `${DASHBOARD_BASE}/hrms/lifecycle`, icon: Workflow },
        //     { name: 'Workplace', href: `${DASHBOARD_BASE}/hrms/workplace`, icon: Package },
        //     ...(isOrgAdmin
        //       ? [{ name: 'HR Settings', href: `${DASHBOARD_BASE}/hrms/settings`, icon: SlidersHorizontal }]
        //       : []),
        //   ],
        // },
        {
          key: 'settings',
          name: 'Settings',
          icon: Settings,
          pinBottom: true,
          items: [
            { name: 'Settings', href: `${DASHBOARD_BASE}/settings`, icon: Settings, primary: true },
            ...devNavigation,
          ],
        },
        // Employee Activity is NOT a nav item — opened from a Dashboard button
        // (EmployeeActivityModal). The employee-activity page is a direct-URL fallback only.
      ];

  // Until organization loads, default to minimal nav to avoid flash of full menu
  const sections = ((!orgLoaded) || restricted ? baseSections : fullSections)
    .filter((s) => s.items.length > 0);

  // Rail selection: follows the route, but a rail click can preview another
  // section's panel without navigating (same as Asana).
  const routeSectionKey = sections.find((s) => s.items.some((i) => isActive(i.href)))?.key;
  const [railKey, setRailKey] = useState(null);
  useEffect(() => {
    if (routeSectionKey) setRailKey(routeSectionKey);
  }, [routeSectionKey]);
  const activeSection = sections.find((s) => s.key === railKey) || sections[0];

  // Redirect to dashboard if plan expired and user is on a different page
  useEffect(() => {
    if (restricted && orgLoaded && location.pathname !== `${DASHBOARD_BASE}` && location.pathname !== `${DASHBOARD_BASE}/settings`) {
      navigate(`${DASHBOARD_BASE}`, { replace: true });
    }
  }, [restricted, orgLoaded, location.pathname, navigate]);

  // OLD_GOALS_PREVIEW: the legacy Tasks page is org-restricted, so hiding the
  // sidebar entry is not enough — bounce anyone else who types the URL directly.
  useEffect(() => {
    if (!orgLoaded || restricted) return;
    if (location.pathname === `${DASHBOARD_BASE}/reports` && !canSeeOldTasks) {
      navigate(`${DASHBOARD_BASE}`, { replace: true });
    }
  }, [location.pathname, canSeeOldTasks, orgLoaded, restricted, navigate]);

  // Redirect if user is on a disabled Goals/Tasks page or not in S5 org
  useEffect(() => {
    if (!orgLoaded || restricted) return;
    const s5Only = isS5Organisation(user);

    // Goals Settings is reachable for S5 admins even when My Goals is off,
    // otherwise a disabled module could never be re-enabled from the Goals area.
    if (location.pathname === `${DASHBOARD_BASE}/goals/settings`) {
      if (!s5Only || user?.role === 'org_user') {
        navigate(`${DASHBOARD_BASE}`, { replace: true });
      }
      return;
    }

    // Redirect from /dashboard/goals if not S5 or My Goals is disabled
    if (location.pathname.startsWith(`${DASHBOARD_BASE}/goals`) && (!s5Only || !goalsSettings?.myGoalsEnabled)) {
      navigate(`${DASHBOARD_BASE}`, { replace: true });
      if (!s5Only) toast.error('Goals are not available for your organisation');
      else toast.error('My Goals module is disabled');
    }

    // Redirect from /dashboard/tasks if not S5 or My Tasks is disabled.
    // ADDITIVE: skip entirely when the org has the new Tasks module enabled —
    // that route now belongs to modules/tasks (org-gated), not the Goals area.
    if (organization?.tasksEnabled !== true
      && location.pathname.startsWith(`${DASHBOARD_BASE}/tasks`) && (!s5Only || !goalsSettings?.myTasksEnabled)) {
      navigate(`${DASHBOARD_BASE}`, { replace: true });
      if (!s5Only) toast.error('Tasks are not available for your organisation');
      else toast.error('My Tasks module is disabled');
    }
  }, [location.pathname, goalsSettings, orgLoaded, restricted, navigate, user, organization]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleUpgrade = () => {
    if (user?.role === 'org_user') {
      toast.error("You don't have permission, contact organization admin");
    } else if (user?.role === 'org_admin') {
      navigate(`${DASHBOARD_BASE}/settings?section=billing`);
    }
  };

  const handleSwitchBack = async () => {
    try {
      const response = await api.post('/api/v1/admin/switch-back');
      if (response.data.success) {
        localStorage.setItem('token', response.data.data.token);
        localStorage.setItem('refreshToken', response.data.data.refreshToken);
        // Clear switch session flags
        localStorage.removeItem('isMainAdminSwitch');
        localStorage.removeItem('mainAdminSwitchUserId');
        toast.success('Switched back to Main Admin');
        window.location.href = '/main-admin';
      }
    } catch (error) {
      toast.error('Failed to switch back');
    }
  };

  // Picking a rail section also opens its first destination. Swapping the panel
  // without moving left people looking at a list they still had to click.
  // Already inside the section → leave them where they are.
  const selectSection = (key) => {
    setRailKey(key);
    const section = sections.find((s) => s.key === key);
    if (!section || section.items.some((i) => isActive(i.href))) return;
    // Skip locked entries so a rail click never lands on the upgrade toast,
    // and coming-soon entries so it never lands on a page we aren't offering.
    const target =
      section.items.find((i) => i.locked !== true && i.soon !== true) || section.items[0];
    if (target) handleNavigation(target.href, target.locked === true);
  };

  const handleNavigation = (href, locked = false) => {
    // If item is locked (Standard plan Org Intelligence), show upgrade prompt
    if (locked) {
      toast.error('Org Intelligence is only available on Premium or Free plan. Please upgrade to access this feature.', {
        duration: 4000,
        icon: '🔒',
      });
      navigate(`${DASHBOARD_BASE}/settings?section=billing`);
      setSidebarOpen(false);
      return;
    }

    // Don't navigate if already on this route
    if (location.pathname === href) {
      setSidebarOpen(false);
      return;
    }

    // Guard: if leaving settings while Goal Settings has unsaved changes
    const isLeavingSettings =
      location.pathname === '/dashboard/settings' &&
      new URLSearchParams(location.search).get('section') === 'goals_settings';

    if (isLeavingSettings && hasUnsavedGoalsChanges) {
      pendingNavHref.current = href;
      setShowUnsavedModal(true);
      setSidebarOpen(false);
      return;
    }

    navigate(href);
    setSidebarOpen(false);
  };

  const proceedNavigation = () => {
    if (pendingNavHref.current) {
      navigate(pendingNavHref.current);
      pendingNavHref.current = null;
    }
    setSidebarOpen(false);
  };

  const handleUnsavedLeave = () => {
    resetGoalsToSaved?.();
    setShowUnsavedModal(false);
    proceedNavigation();
  };

  const handleUnsavedSaveAndLeave = async () => {
    setSavingOnLeave(true);
    try {
      await saveGoalsSettings?.();
    } catch (_) {
      // errors already toasted by saveSettings
    } finally {
      setSavingOnLeave(false);
    }
    setShowUnsavedModal(false);
    proceedNavigation();
  };

  const handleUnsavedCancel = () => {
    setShowUnsavedModal(false);
    pendingNavHref.current = null;
  };

  // Create menu — only destinations this user can actually reach.
  // canManageProjects now also passes for merely-ASSIGNED members (nav
  // visibility); creating stays a real right — admins or addProject only.
  const hasAddProjectRight = user?.role === 'main_admin' || user?.role === 'org_admin'
    || (user?.role === 'org_user' && !!user?.permissions?.addProject);
  const createActions = restricted ? [] : [
    ...(hasAddProjectRight
      ? [{ label: 'New project', icon: FolderOpen, onSelect: () => handleNavigation(`${DASHBOARD_BASE}/projects`) }]
      : []),
    { label: 'Invite team member', icon: Users, onSelect: () => handleNavigation(`${DASHBOARD_BASE}/team`) },
  ];

  return (
    <div className="tg-asana tg-shell h-screen flex flex-col overflow-hidden text-app-primary transition-colors duration-300">
      <AppTopBar
        sections={sections}
        createActions={createActions}
        onNavigate={handleNavigation}
        onToggleSidebar={toggleSidebar}
        user={user}
        isDark={isDark}
        onToggleTheme={toggle}
        onLogout={handleLogout}
        trialDaysRemaining={trialDaysRemaining}
        onUpgrade={handleUpgrade}
        showSwitchBack={isActiveMainAdminSwitch && Boolean(user?.originalMainAdminId)}
        onSwitchBack={handleSwitchBack}
        tasksEnabled={organization?.tasksEnabled === true}
        idleCount={pendingApprovals}
        idleHref={isOrgAdmin ? `${DASHBOARD_BASE}/inbox` : undefined}
      />
      <div className="flex flex-1 overflow-hidden">
      <AppSidebar
        sections={sections}
        activeSection={activeSection}
        onSelectSection={selectSection}
        isActive={isActive}
        onNavigate={handleNavigation}
        collapsed={sidebarCollapsed}
        mobileOpen={sidebarOpen}
        onCloseMobile={() => setSidebarOpen(false)}
        footerItem={{ name: 'Download tracker', href: '/download', icon: MonitorDown, newTab: true }}
      />

      {/* Main content */}
      <div className="tg-content flex flex-col w-0 flex-1 overflow-hidden bg-app-base">
        <main ref={mainRef} className="flex-1 min-h-0 relative z-0 overflow-y-auto focus:outline-none">
          <div
            id="app-main-content"
            className="h-full min-h-0 relative overflow-y-auto"
          >
            <Outlet />
          </div>
        </main>
      </div>
      </div>

      {/* Plan-expired / renewal blocking overlay — same flow as the legacy
          Dashboard. Renders a fixed portal modal over everything when the
          org/user plan is restricted (trial/subscription expired). */}
      <PlanExpiredOverlay
        restricted={restricted}
        orgLoaded={orgLoaded}
        organization={organization}
        user={user}
        logout={logout}
      />

      {/* Floating Chat Button — hide on Ask TG itself */}
      {orgLoaded &&
       !restricted &&
       user &&
       user.role !== 'main_admin' &&
       location.pathname !== `${DASHBOARD_BASE}/ask` &&
       location.pathname !== `${DASHBOARD_BASE}/ask-new` &&
       location.pathname !== '/dashboard/ai-search' && (
        <FloatingChatButton />
      )}
      {orgLoaded &&
       !restricted &&
       user &&
       (user.role === 'org_admin' || user.isMainAdminSwitch) && (
        <FloatingSuggestButton />
      )}

      {/* Unsaved Goal Settings confirmation modal */}
      <Modal
        open={showUnsavedModal}
        onCancel={handleUnsavedCancel}
        footer={null}
        centered
        closable={false}
        width={440}
        styles={{
          content: {
            background: isDark ? '#0f1117' : '#ffffff',
            border: isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e5e7eb',
            borderRadius: '20px',
            padding: '0',
            overflow: 'hidden',
            boxShadow: isDark
              ? '0 25px 50px rgba(0,0,0,0.6)'
              : '0 25px 50px rgba(0,0,0,0.12)',
          },
          mask: { backdropFilter: 'blur(4px)', background: 'rgba(0,0,0,0.45)' }
        }}
      >
        {/* Amber top accent bar */}
        <div className="h-1 w-full bg-gradient-to-r from-amber-400 via-orange-400 to-amber-500" />

        <div className="px-7 pt-6 pb-7 flex flex-col gap-5">
          {/* Icon + heading */}
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 w-11 h-11 rounded-2xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center">
              <AlertTriangle size={20} className="text-amber-500" strokeWidth={2.2} />
            </div>
            <div className="pt-0.5">
              <h3 className={`text-[15px] font-bold tracking-tight ${isDark ? 'text-white' : 'text-gray-900'}`}>
                You have unsaved changes
              </h3>
              <p className={`text-[13px] mt-1 leading-relaxed ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                Your Goal Settings edits haven't been saved yet. What would you like to do?
              </p>
            </div>
          </div>

          {/* Divider */}
          <div className={`h-px w-full ${isDark ? 'bg-white/[0.06]' : 'bg-gray-100'}`} />

          {/* Action cards */}
          <div className="flex flex-col gap-2.5">
            <button
              onClick={handleUnsavedSaveAndLeave}
              disabled={savingOnLeave}
              className={`group w-full flex items-center gap-3.5 px-4 py-3.5 rounded-xl border text-left transition-all duration-150 ${
                isDark
                  ? 'bg-blue-600/10 border-blue-500/25 hover:bg-blue-600/20 hover:border-blue-500/40'
                  : 'bg-blue-50 border-blue-200 hover:bg-blue-100 hover:border-blue-300'
              } disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center shadow-sm">
                <Save size={14} className="!text-white" strokeWidth={2.5} />
              </div>
              <div>
                <p className={`text-[13px] font-semibold ${isDark ? 'text-blue-300' : 'text-blue-700'}`}>
                  {savingOnLeave ? 'Saving...' : 'Save changes & leave'}
                </p>
                <p className={`text-[11px] mt-0.5 ${isDark ? 'text-blue-400/60' : 'text-blue-500/70'}`}>
                  Save your edits, then navigate away
                </p>
              </div>
            </button>

            <button
              onClick={handleUnsavedLeave}
              className={`group w-full flex items-center gap-3.5 px-4 py-3.5 rounded-xl border text-left transition-all duration-150 ${
                isDark
                  ? 'bg-white/[0.03] border-white/[0.07] hover:bg-red-500/10 hover:border-red-500/20'
                  : 'bg-gray-50 border-gray-200 hover:bg-red-50 hover:border-red-200'
              }`}
            >
              <div className={`flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center border transition-colors ${
                isDark
                  ? 'bg-white/5 border-white/10 group-hover:bg-red-500/20 group-hover:border-red-500/30'
                  : 'bg-white border-gray-200 group-hover:bg-red-100 group-hover:border-red-200'
              }`}>
                <X size={14} className={`transition-colors ${isDark ? 'text-gray-400 group-hover:text-red-400' : 'text-gray-400 group-hover:text-red-500'}`} strokeWidth={2.5} />
              </div>
              <div>
                <p className={`text-[13px] font-semibold transition-colors ${isDark ? 'text-gray-300 group-hover:text-red-400' : 'text-gray-700 group-hover:text-red-600'}`}>
                  Discard changes & leave
                </p>
                <p className={`text-[11px] mt-0.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                  Your unsaved edits will be lost
                </p>
              </div>
            </button>
          </div>

          {/* Cancel button */}
          <button
            onClick={handleUnsavedCancel}
            className={`w-full py-2.5 rounded-xl text-[13px] font-medium border transition-all duration-150 ${
              isDark
                ? 'bg-transparent border-white/10 text-gray-400 hover:bg-white/5 hover:text-gray-200 hover:border-white/20'
                : 'bg-transparent border-gray-200 text-gray-500 hover:bg-gray-50 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Keep editing
          </button>
        </div>
      </Modal>
    </div>
  );
};

// Memoize Layout to prevent re-renders when only location changes
// Layout should only re-render when user, organization, or theme changes
export default React.memo(Layout);
